/**
 * Retry Configuration
 *
 * Centralized configuration for retry logic across the application.
 * Supports different retry strategies for different types of operations.
 */

export enum RetryStrategy {
  EXPONENTIAL = "exponential",
  LINEAR = "linear",
  FIXED = "fixed",
}

export interface RetryConfig {
  maxAttempts: number;
  strategy: RetryStrategy;
  baseDelay: number; // milliseconds
  maxDelay: number; // milliseconds
  retryableStatusCodes: number[];
  retryableErrors: string[];
  shouldRetry?: (error: unknown, attemptCount: number) => boolean;
}

export interface RetryOptions {
  maxAttempts?: number;
  strategy?: RetryStrategy;
  baseDelay?: number;
  maxDelay?: number;
  onRetry?: (error: unknown, attemptCount: number, delay: number) => void;
}

/**
 * Default retry configurations for different operation types
 */
export const DEFAULT_RETRY_CONFIGS: Record<string, RetryConfig> = {
  http: {
    maxAttempts: 3,
    strategy: RetryStrategy.EXPONENTIAL,
    baseDelay: 1000,
    maxDelay: 10000,
    retryableStatusCodes: [408, 429, 500, 502, 503, 504],
    retryableErrors: ["ECONNREFUSED", "ETIMEDOUT", "ENOTFOUND", "Network Error"],
  },
  websocket: {
    maxAttempts: 5,
    strategy: RetryStrategy.EXPONENTIAL,
    baseDelay: 1000,
    maxDelay: 30000,
    retryableStatusCodes: [],
    retryableErrors: ["Connection closed", "Connection failed", "Timeout"],
  },
  signalr: {
    maxAttempts: 5,
    strategy: RetryStrategy.EXPONENTIAL,
    baseDelay: 1000,
    maxDelay: 30000,
    retryableStatusCodes: [],
    retryableErrors: ["Connection closed", "Connection failed", "Timeout", "HubException"],
  },
  tokenRefresh: {
    maxAttempts: 2,
    strategy: RetryStrategy.FIXED,
    baseDelay: 500,
    maxDelay: 500,
    retryableStatusCodes: [500, 502, 503, 504],
    retryableErrors: [],
  },
  mutation: {
    maxAttempts: 2,
    strategy: RetryStrategy.LINEAR,
    baseDelay: 2000,
    maxDelay: 5000,
    retryableStatusCodes: [408, 500, 502, 503, 504],
    retryableErrors: [],
  },
  query: {
    maxAttempts: 3,
    strategy: RetryStrategy.EXPONENTIAL,
    baseDelay: 500,
    maxDelay: 5000,
    retryableStatusCodes: [408, 500, 502, 503, 504],
    retryableErrors: [],
  },
};

/**
 * Calculate delay based on retry strategy
 */
export function calculateDelay(
  attemptCount: number,
  config: RetryConfig
): number {
  let delay: number;

  switch (config.strategy) {
    case RetryStrategy.EXPONENTIAL:
      delay = Math.min(
        config.baseDelay * Math.pow(2, attemptCount - 1),
        config.maxDelay
      );
      break;

    case RetryStrategy.LINEAR:
      delay = Math.min(config.baseDelay * attemptCount, config.maxDelay);
      break;

    case RetryStrategy.FIXED:
      delay = config.baseDelay;
      break;

    default:
      delay = config.baseDelay;
  }

  // Add jitter (+/- 25%) to prevent thundering herd
  const jitter = delay * 0.25 * (Math.random() * 2 - 1);
  return Math.round(delay + jitter);
}

/**
 * Check if error is retryable based on config
 */
export function isRetryableError(error: unknown, config: RetryConfig): boolean {
  // Check custom retry logic first
  if (config.shouldRetry) {
    return config.shouldRetry(error, 0);
  }

  const err = error as { response?: { status?: number }; message?: string; code?: string };

  // Check status code
  if (err.response?.status) {
    return config.retryableStatusCodes.includes(err.response.status);
  }

  // Check error message
  if (err.message) {
    return config.retryableErrors.some((retryableError) =>
      err.message?.includes(retryableError)
    );
  }

  // Check error code
  if (err.code) {
    return config.retryableErrors.includes(err.code);
  }

  return false;
}

/**
 * Retry wrapper function with configurable strategy
 */
export async function retryWithConfig<T>(
  operation: () => Promise<T>,
  config: RetryConfig,
  options: RetryOptions = {}
): Promise<T> {
  const maxAttempts = options.maxAttempts ?? config.maxAttempts;
  let attemptCount = 0;
  let lastError: unknown;

  while (attemptCount < maxAttempts) {
    attemptCount++;

    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Check if we should retry
      const shouldRetry =
        attemptCount < maxAttempts &&
        (config.shouldRetry
          ? config.shouldRetry(error, attemptCount)
          : isRetryableError(error, config));

      if (!shouldRetry) {
        throw error;
      }

      // Calculate delay
      const delay = calculateDelay(attemptCount, {
        ...config,
        strategy: options.strategy ?? config.strategy,
        baseDelay: options.baseDelay ?? config.baseDelay,
        maxDelay: options.maxDelay ?? config.maxDelay,
      });

      // Call retry callback
      if (options.onRetry) {
        options.onRetry(error, attemptCount, delay);
      }

      console.warn(
        `Retry attempt ${attemptCount}/${maxAttempts} after ${delay}ms`,
        error
      );

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

/**
 * Create a retry wrapper with preset config
 */
export function createRetryWrapper(
  configName: keyof typeof DEFAULT_RETRY_CONFIGS
) {
  const config = DEFAULT_RETRY_CONFIGS[configName];

  return <T>(
    operation: () => Promise<T>,
    options: RetryOptions = {}
  ): Promise<T> => {
    return retryWithConfig(operation, config, options);
  };
}

// Preset retry wrappers
export const retryHttp = createRetryWrapper("http");
export const retryWebSocket = createRetryWrapper("websocket");
export const retrySignalR = createRetryWrapper("signalr");
export const retryTokenRefresh = createRetryWrapper("tokenRefresh");
export const retryMutation = createRetryWrapper("mutation");
export const retryQuery = createRetryWrapper("query");

/**
 * Update retry config at runtime
 */
export function updateRetryConfig(
  configName: keyof typeof DEFAULT_RETRY_CONFIGS,
  updates: Partial<RetryConfig>
): void {
  DEFAULT_RETRY_CONFIGS[configName] = {
    ...DEFAULT_RETRY_CONFIGS[configName],
    ...updates,
  };
  console.log(`Updated retry config for ${configName}:`, updates);
}

/**
 * Get current retry config
 */
export function getRetryConfig(
  configName: keyof typeof DEFAULT_RETRY_CONFIGS
): RetryConfig {
  return { ...DEFAULT_RETRY_CONFIGS[configName] };
}
